import type { Bundle, CodeableConcept } from 'fhir/r4';
import type { Communication } from 'fhir/r4';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Loader, Message, Table, Icon, Popup, Item } from 'semantic-ui-react';
import type { SemanticICONS, SemanticCOLORS } from 'semantic-ui-react';
import useSWR from 'swr';
import type { ResourceEntry } from 'utils/bundleNarrowing';

import { useAuthContext } from '../../../contexts/AuthContext';
import FHIRFetcher from '../../../data/FHIRFetcher';


import { EMAIL_TEXT_SUMMARY } from './summary';
import type { CarebettyProps, ChannelIconProps } from './types';

function Carebetty(props: CarebettyProps) {
  useEffect(() => {
    if (props.activeTab === 'COMMUNICATION') {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
      communicationMutate(); //this will trigger SWR to re-Fetch
    }
     
  }, [props.activeTab]);

  const authInfo = useAuthContext();

  const [communicationsData, setCommunicationsData] = useState<Bundle>();

  const [loading, setLoading] = React.useState(true);
  const options = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    /*
     * SUSPECTED DEFECT — three disagreements in this five-line callback, all
     * hidden until the parameter was typed.
     *
     * 1. `data` is declared `Bundle | undefined` (SWR 0.5.7 hands the fetcher's
     *    resolved value straight through, and `FHIRFetcher` can resolve to
     *    `undefined`) but is dereferenced unguarded on the very first line. If a
     *    fetch ever resolves without a bundle this throws inside SWR's success
     *    path.
     *
     * 2. `entry.resource.medium` — `BundleEntry.resource` is `0..1` and typed as
     *    the whole `FhirResource` union, on which `medium` does not exist. It is
     *    a `Communication` field. The `/Communication` search makes the
     *    assumption hold in practice, so this is latent, but the read is
     *    unguarded on a genuinely optional field.
     *
     * 3. `data.entry = ... : null` writes `null` into a field FHIR declares
     *    `BundleEntry[] | undefined`. That is deliberate — the JSX below
     *    branches on `communicationsData.entry` being falsy to show the "no
     *    communications" message — but it means every downstream reader of this
     *    bundle sees a shape FHIR says is impossible. The `?.length > 0`
     *    comparison on the same line is the familiar `undefined > 0` idiom that
     *    is accidentally right.
     *
     * Not fixed: the `null` write is load-bearing for the empty-state render,
     * and guarding `data` would skip a `setLoading(false)` that runs today.
     */
    onSuccess: (data: Bundle | undefined, _key: string, _config: unknown) => {
      // also filter out zendesk comms, doesn't work in url
      data!.entry = data!.entry?.filter(entry => (
        !(entry.resource as Communication).medium?.find((m: CodeableConcept) => m.text === 'Zendesk')
      ));
      data!.entry = ((data!.entry?.length as number) > 0 ? data!.entry : null) as Bundle['entry'];
      setCommunicationsData(data);
      setLoading(false);
    },
  };
  let url: string = '/Communication/'
    + '?_sort=-sent'
    + '&status=completed'
    + '&identifier:not=https://projectwell.io/fhir/identifiers/communication/contact-state|' //filter out connect communications
    + '&medium:not=zoom' // filter out zoom communications
  if (props?.patient) {
    url += '&patient=' + props.patient.id;
  }

  const {
    //data: communicationsData,
    error: communicationsError,
    mutate: communicationMutate,
  } = useSWR<Bundle, Error>([url, authInfo.token], FHIRFetcher as () => Promise<Bundle>, options);

  return (
    <div>
      {loading && !communicationsData && !communicationsError && <Loader active inline="centered" />}

      {communicationsData && !communicationsData.entry && (
        <Message warning header="Info" content="No communications found." />
      )}
      {communicationsData && communicationsData.entry && (
        <Table className="table-div" basic sortable celled singleLine>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Channel</Table.HeaderCell>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Subject</Table.HeaderCell>
              <Table.HeaderCell>DateTime</Table.HeaderCell>
              <Table.HeaderCell>Performed by</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {(communicationsData.entry as ResourceEntry<'Communication'>[]).map((communication) => {
              const commResource = communication && communication.resource;
              const commType = commResource.medium && commResource.medium[0] && commResource.medium[0].text;
              const commSubject = commResource.topic && commResource.topic.text;
              const practitioner = commResource.sender && commResource.sender.display;
              const source = commResource?.medium && commResource?.medium?.[1] && commResource?.medium?.[1]?.text;
              const org = props?.patient?.org;
              /*
               * SUSPECTED DEFECT — `EMAIL_TEXT_SUMMARY` declares `commType:
               * string`, but the value passed can be `undefined`.
               *
               * `commType` is built above as `commResource.medium &&
               * commResource.medium[0] && commResource.medium[0].text`, and
               * `Communication.medium` is `0..*` with `CodeableConcept.text`
               * `0..1` — so a Communication with no medium, or a medium with no
               * text, yields `undefined` (or the empty-array falsy value) rather
               * than a string.
               *
               * The helper switches on `commType` to choose the summary
               * wording; an `undefined` falls through its branches, so such a
               * row renders whatever its default arm produces rather than a
               * channel-appropriate summary. The same value is also handed to
               * `<ChannelIcon channel={commType} />` below, where it lands on
               * the `default:` arm and shows the green phone icon for a
               * communication that may not be a call at all.
               *
               * Asserted rather than defaulted: substituting a fallback string
               * would change which summary text and which icon these rows show.
               */
              const emailOrTextTemplate =
                org === 'HA' || org === 'IU'
                  ? EMAIL_TEXT_SUMMARY(commType as string, commSubject, org, source ?? undefined, props?.patient)
                  : EMAIL_TEXT_SUMMARY(commType as string, commSubject, 'GENERIC', source ?? undefined, props?.patient);
              return (
                <Table.Row key={communication.id}>
                  <Table.Cell>
                    <ChannelIcon channel={commType} />
                  </Table.Cell>
                  <Table.Cell>{isOutbound(commResource) ? 'outbound' : 'inbound'}</Table.Cell>
                  <Table.Cell>
                    <Popup
                      trigger={<Item.Description>{commSubject}</Item.Description>}
                      wide="very"
                      content={emailOrTextTemplate}
                      on="hover"
                      position="bottom right"
                    />
                  </Table.Cell>
                  <Table.Cell>
                    {commResource?.sent
                      ? moment.utc(commResource.sent).local().format('YYYY-MM-DD hh:mm:ss a')
                      : moment.utc(commResource.received).local().format('YYYY-MM-DD hh:mm:ss a')}
                  </Table.Cell>
                  <Table.Cell>
                    {practitioner} {communicationSource(source)}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}

      {communicationsError && (
        <Message negative>
          <Message.Header>Oops!. Something went wrong.. </Message.Header>
          <p>{communicationsError.message}</p>
        </Message>
      )}
    </div>
  );
}

//some communications may be inbound so check
const isOutbound = (commResource: Communication | undefined): boolean => {
  if (commResource?.received) return false;
  else return true;
};

/*
 * SUSPECTED DEFECT — `type` selects the icon colour but nothing ever passes it.
 *
 * The `voice` arm below reads `props.type` to colour outbound calls orange and
 * voicemails purple. The one call site in this file is `<ChannelIcon
 * channel={commType} />` — no `type` attribute — so `props.type` is always
 * `undefined`, both branches are dead, and every voice row renders with the
 * default green regardless of direction.
 *
 * LIVE: the outbound/inbound distinction IS available at the call site (the
 * adjacent cell renders `isOutbound(commResource) ? 'outbound' : 'inbound'`),
 * so the information exists and is simply not threaded in.
 *
 * Not fixed. Passing it would change the colour of rows that render green
 * today.
 */
const ChannelIcon = (props: ChannelIconProps) => {
  let iconName: SemanticICONS = 'phone volume';
  let iconColor: SemanticCOLORS = 'green';

  switch (props.channel) {
    case 'voice':
      iconName = 'phone volume';
      if (props.type === 'outbound') {
        iconColor = 'orange';
      } else if (props.type === 'voicemail') {
        iconColor = 'purple';
      }
      break;
    case 'email':
      iconName = 'mail outline';
      iconColor = 'grey';
      break;
    case 'sms':
      iconName = 'chat';
      iconColor = 'blue';
      break;
    case 'email-survey':
      iconName = 'mail outline';
      iconColor = 'grey';
      break;
    case 'sms-survey':
      iconName = 'chat';
      iconColor = 'blue';
      break;
    default:
      break;
  }

  return <Icon color={iconColor} name={iconName} size="large" />;
};

function communicationSource(source: string | undefined) {
  if (source) {
    if (source.includes('launch')) {
      return ' (launch)';
    } else if (source.includes('auto')) {
      return ' (auto)';
    } else if (source.includes('web')) {
      return ' (web)';
    } else if (source.includes('envelop')) {
      return ' (envelop)';
    }
  } else {
    return '';
  }
}
export default Carebetty;
